/**
 * ADXPivotTable - Data-Dense Multi-Timeframe ADX/ATR Matrix
 *
 * Displays ADX and ATR data in compact pivot-table format.
 * Shows H4 and D1 timeframes with ADX value, range, ATR, implications.
 * Maximum data density with minimal vertical space.
 *
 * PHASE 1.1/1.2: Uses QAI Design Tokens for institutional-grade typography
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import {
    QAI_TYPE_SCALE,
    QAI_FONTS,
    QAI_TRACKING,
    QAI_LAYOUT,
    QAI_COLORS
} from '../../../theme/qaiDesignTokens';

/**
 * Get ADX range color
 */
const getAdxColor = (value, range) => {
    if (!value && !range) return '#6b7280';
    const adx = parseFloat(value) || 0;
    const r = range?.toUpperCase() || '';

    if (adx >= 40 || r.includes('STRONG')) return '#22c55e';
    if (adx >= 25 || r.includes('GOOD')) return '#4ade80';
    if (adx >= 15 || r.includes('WEAK')) return '#fbbf24';
    return '#9ca3af';
};

/**
 * Get range short label
 */
const getRangeShort = (range) => {
    if (!range) return '--';
    const r = range.toUpperCase();
    if (r.includes('STRONG')) return 'STRNG';
    if (r.includes('GOOD')) return 'GOOD';
    if (r.includes('WEAK')) return 'WEAK';
    if (r.includes('RANGING') || r.includes('RANGE')) return 'RANGE';
    return r.substring(0, 5);
};

/**
 * Get implication text
 */
const getImplication = (range) => {
    if (!range) return '--';
    const r = range.toUpperCase();
    if (r.includes('STRONG')) return 'Strong Move';
    if (r.includes('GOOD')) return 'Trending';
    if (r.includes('WEAK')) return 'Weak Trend';
    return 'Ranging';
};

/**
 * Table Header Cell - PHASE 3.1: Compact 32px rows with 11px UPPERCASE headers
 */
const HeaderCell = ({ children, width = 'auto', align = 'center' }) => (
    <Box
        sx={{
            width,
            minWidth: width !== 'auto' ? width : '50px',
            padding: QAI_LAYOUT.paddingCell,  // 4px 6px
            textAlign: align,
            borderRight: `1px solid ${QAI_COLORS.border.subtle}`,
            '&:last-child': { borderRight: 'none' }
        }}
    >
        <Typography
            sx={{
                fontSize: QAI_TYPE_SCALE.xs,  // 11px - per spec
                fontWeight: 600,
                color: QAI_COLORS.text.muted,
                fontFamily: QAI_FONTS.mono,
                textTransform: 'uppercase',
                letterSpacing: QAI_TRACKING.wide  // 0.05em
            }}
        >
            {children}
        </Typography>
    </Box>
);

/**
 * Table Data Cell - PHASE 3.1: 13px data with monospace numbers
 */
const DataCell = ({ children, color = '#fff', width = 'auto', align = 'center', bold = false }) => (
    <Box
        sx={{
            width,
            minWidth: width !== 'auto' ? width : '50px',
            padding: QAI_LAYOUT.paddingCell,  // 4px 6px
            textAlign: align,
            borderRight: `1px solid ${QAI_COLORS.border.subtle}`,
            '&:last-child': { borderRight: 'none' }
        }}
    >
        <Typography
            sx={{
                fontSize: QAI_TYPE_SCALE.sm,  // 13px - per spec
                fontWeight: bold ? 700 : 500,
                color,
                fontFamily: QAI_FONTS.mono,
                letterSpacing: QAI_TRACKING.tight  // -0.01em for numbers
            }}
        >
            {children}
        </Typography>
    </Box>
);

/**
 * ADX Progress Mini Bar
 * T7 FIX: ADX must be 0-100 range. Values >100 indicate data error.
 * PHASE 3.1: Updated to use design tokens
 */
const AdxMiniBar = ({ value }) => {
    const rawAdx = parseFloat(value) || 0;
    // T7: Validate ADX range - values >100 are invalid (ADX max is 100)
    const isValid = rawAdx >= 0 && rawAdx <= 100;
    const adx = isValid ? rawAdx : 0;
    const percentage = Math.min(adx / 60 * 100, 100);
    const color = isValid ? getAdxColor(adx) : QAI_COLORS.text.muted;
    const displayValue = isValid ? adx.toFixed(1) : 'N/A';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            <Typography sx={{
                fontSize: QAI_TYPE_SCALE.sm,  // 13px - readable
                color,
                fontWeight: 700,
                fontFamily: QAI_FONTS.mono,
                minWidth: '32px',
                letterSpacing: QAI_TRACKING.tight
            }}>
                {displayValue}
            </Typography>
            <Box sx={{
                width: '30px',
                height: '4px',
                backgroundColor: QAI_COLORS.background.surface2,
                borderRadius: '2px',
                overflow: 'hidden'
            }}>
                <Box sx={{ width: `${percentage}%`, height: '100%', backgroundColor: color, borderRadius: '2px' }} />
            </Box>
        </Box>
    );
};

/**
 * ADXPivotTable Component
 *
 * Props:
 * - data: From strategyDashboard.adxAnalysis (fallback)
 * - currentState: Real-time state with volatility
 * - adxAnalysis: Task 1.2 - Extended ADX analysis data
 */
const ADXPivotTable = memo(({ data, currentState, adxAnalysis }) => {
    // Extract volatility data from currentState (API response structure)
    // API returns: currentState.volatility { h4Adx, adxRange, h4Atr }
    const volatility = currentState?.volatility || {};

    // Task 1.2: Use extended adxAnalysis when available
    const extH4 = adxAnalysis?.h4 || {};
    const extD1 = adxAnalysis?.d1 || {};

    // H4 values - prefer extended data, fallback to currentState
    const h4AdxValue = extH4.adx ?? volatility.h4Adx;
    const h4AdxRange = extH4.range ?? volatility.adxRange;
    const h4AtrValue = extH4.atr ?? volatility.h4Atr;
    const h4PlusDI = extH4.plusDI;   // Task 1.2: New field
    const h4MinusDI = extH4.minusDI; // Task 1.2: New field

    // D1 values - from extended data only (not in currentState)
    const d1AdxValue = extD1.adx;
    const d1AdxRange = extD1.range;
    const d1AtrValue = extD1.atr;

    // Volatility regime from extended analysis
    const regime = adxAnalysis?.regime;  // Task 1.2: TRENDING_UP, TRENDING_DOWN, RANGING

    // Calculate ATR percentage based on current price
    const currentPrice = currentState?.price || 0;
    const h4AtrPercent = currentPrice > 0 && h4AtrValue ? (h4AtrValue / currentPrice * 100) : 0;
    const d1AtrPercent = currentPrice > 0 && d1AtrValue ? (d1AtrValue / currentPrice * 100) : 0;

    const timeframes = [
        {
            tf: 'H4',
            adx: h4AdxValue,
            range: h4AdxRange,
            atr: h4AtrValue,
            atrPct: h4AtrPercent,
            plusDI: h4PlusDI,   // Task 1.2
            minusDI: h4MinusDI  // Task 1.2
        },
        {
            tf: 'D1',
            adx: d1AdxValue ?? 0,  // Task 1.2: Now populated from extended API
            range: d1AdxRange ?? '--',
            atr: d1AtrValue ?? h4AtrValue, // Fallback to H4 ATR if D1 not available
            atrPct: d1AtrPercent || h4AtrPercent
        }
    ];

    // Check if we have D1 data from extended analysis
    const hasD1Data = !!d1AdxValue;

    return (
        <Box sx={{
            backgroundColor: QAI_COLORS.background.primary,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${QAI_COLORS.border.subtle}`
        }}>
            {/* Table Header - PHASE 3.1: 36px header height */}
            <Box
                sx={{
                    display: 'flex',
                    height: QAI_LAYOUT.rowNormal,  // 36px
                    backgroundColor: QAI_COLORS.semantic.infoMuted,
                    borderBottom: `1px solid ${QAI_COLORS.semantic.infoBorder}`
                }}
            >
                <HeaderCell width="35px">TF</HeaderCell>
                <HeaderCell width="70px">ADX</HeaderCell>
                <HeaderCell width="50px">RANGE</HeaderCell>
                <HeaderCell width="55px">ATR</HeaderCell>
                <HeaderCell width="45px">ATR%</HeaderCell>
                <HeaderCell width="70px">STATUS</HeaderCell>
            </Box>

            {/* Table Body - PHASE 3.1: 32px compact rows */}
            {timeframes.map((row, index) => (
                <Box
                    key={row.tf}
                    sx={{
                        display: 'flex',
                        height: QAI_LAYOUT.rowCompact,  // 32px compact
                        alignItems: 'center',
                        borderBottom: index < timeframes.length - 1 ? `1px solid ${QAI_COLORS.border.subtle}` : 'none',
                        '&:hover': { backgroundColor: QAI_COLORS.background.surface2 }
                    }}
                >
                    <DataCell width="35px" color={QAI_COLORS.semantic.info} bold>{row.tf}</DataCell>
                    <Box sx={{
                        width: '70px',
                        padding: QAI_LAYOUT.paddingCell,
                        borderRight: `1px solid ${QAI_COLORS.border.subtle}`
                    }}>
                        <AdxMiniBar value={row.adx} />
                    </Box>
                    <DataCell width="50px" color={getAdxColor(row.adx, row.range)} bold>
                        {getRangeShort(row.range)}
                    </DataCell>
                    <DataCell width="55px" color={QAI_COLORS.text.secondary}>
                        {row.atr ? row.atr.toFixed(4) : '--'}
                    </DataCell>
                    <DataCell width="45px" color={row.atrPct > 1 ? QAI_COLORS.semantic.neutral : QAI_COLORS.text.muted}>
                        {row.atrPct ? `${row.atrPct.toFixed(2)}%` : '--'}
                    </DataCell>
                    <DataCell width="70px" color={getAdxColor(row.adx, row.range)}>
                        {getImplication(row.range)}
                    </DataCell>
                </Box>
            ))}

            {/* Summary Row with Regime Indicator (GAP-3) - PHASE 3.1 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: QAI_COLORS.background.surface2,
                    borderTop: `1px solid ${QAI_COLORS.border.default}`,
                    padding: QAI_LAYOUT.paddingCell
                }}
            >
                <Typography sx={{
                    fontSize: QAI_TYPE_SCALE['2xs'],  // 10px micro label
                    color: QAI_COLORS.text.muted,
                    fontFamily: QAI_FONTS.mono
                }}>
                    ATR = Average True Range (Volatility)
                </Typography>

                {/* GAP-3: Regime Indicator from extended adxAnalysis */}
                {regime && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: regime.includes('UP') ? QAI_COLORS.semantic.bullishMuted :
                                regime.includes('DOWN') ? QAI_COLORS.semantic.bearishMuted :
                                    QAI_COLORS.semantic.neutralMuted,
                            border: `1px solid ${regime.includes('UP') ? QAI_COLORS.semantic.bullishBorder :
                                regime.includes('DOWN') ? QAI_COLORS.semantic.bearishBorder :
                                    QAI_COLORS.semantic.neutralBorder}`
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                                fontWeight: 700,
                                fontFamily: QAI_FONTS.mono,
                                letterSpacing: QAI_TRACKING.wide,
                                color: regime.includes('UP') ? QAI_COLORS.semantic.bullish :
                                    regime.includes('DOWN') ? QAI_COLORS.semantic.bearish :
                                        QAI_COLORS.semantic.neutral,
                                textTransform: 'uppercase'
                            }}
                        >
                            {regime.replace('_', ' ')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

ADXPivotTable.displayName = 'ADXPivotTable';

export default ADXPivotTable;
